import { BaseOAuthProvider } from '../BaseOAuthProvider';
import { OAuthProviderConfig, OAuthTokens, RawUserInfo, NormalizedUserInfo } from '../types';

/**
 * Google OAuth 2.0 Provider Implementation
 */
export class GoogleProvider extends BaseOAuthProvider {
  private clientSecret?: string;

  constructor(config: OAuthProviderConfig & { clientSecret?: string }) {
    super({
      id: 'google',
      name: 'Google',
      clientId: config.clientId,
      scopes: config.scopes || ['openid', 'email', 'profile'],
      authorizationEndpoint: 'https://accounts.google.com/o/oauth2/v2/auth',
      tokenEndpoint: 'https://oauth2.googleapis.com/token',
      userInfoEndpoint: 'https://www.googleapis.com/oauth2/v2/userinfo',
    });
    this.clientSecret = config.clientSecret;
  }

  buildAuthorizationUrl(redirectUri: string, state: string, codeVerifier?: string): string {
    const params: Record<string, string> = {
      client_id: this.clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: this.scopes.join(' '),
      state,
      access_type: 'offline',
      prompt: 'consent',
    };

    // Add PKCE if code verifier is provided
    if (codeVerifier) {
      // Note: Code challenge will be generated and added by the handler
      // This is a simplified version - PKCE can be added later if needed
    }

    const queryString = this.buildQueryString(params);
    return `${this.authorizationEndpoint}?${queryString}`;
  }

  async exchangeCodeForTokens(
    code: string,
    redirectUri: string,
    codeVerifier?: string
  ): Promise<OAuthTokens> {
    if (!this.clientSecret) {
      throw new Error('Google OAuth client secret is required for token exchange');
    }

    const body = new URLSearchParams({
      code,
      client_id: this.clientId,
      client_secret: this.clientSecret,
      redirect_uri: redirectUri,
      grant_type: 'authorization_code',
    });

    // Add PKCE if provided
    if (codeVerifier) {
      body.append('code_verifier', codeVerifier);
    }

    const response = await fetch(this.tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Token exchange failed: ${response.status} - ${errorText}`);
    }

    const tokens = await response.json();
    return {
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token,
      expires_in: tokens.expires_in,
      token_type: tokens.token_type || 'Bearer',
      scope: tokens.scope,
    };
  }

  async fetchUserInfo(accessToken: string): Promise<RawUserInfo> {
    const response = await fetch(this.userInfoEndpoint, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch user info: ${response.status} - ${errorText}`);
    }

    return await response.json();
  }

  mapMetadata(rawInfo: RawUserInfo): NormalizedUserInfo {
    const name = rawInfo.name || '';
    const { firstName, lastName } = this.splitFullName(name);

    return {
      email: rawInfo.email || '',
      firstName,
      lastName,
      avatarUrl: rawInfo.picture || null,
      emailVerified: rawInfo.verified_email === true || rawInfo.email_verified === true,
      provider: 'google',
    };
  }
}
